import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, Col, Input, Label, Modal, ModalBody, Row, Spinner } from "@/Components/Common/Tailwind";
import { API, BACKEND_HOST } from "../../api";
import CommonDatePicker from "@/Components/Common/CommonDatePicker";

interface TargetData {
  id: number;
  package_id: number;
  name: string;
  reservationDate: string;
  reservationEndDate?: string;
  spot: number;
}

interface DateChangeModalProps {
  visible: boolean;
  toggle: () => void;
  targetData: TargetData | null;
  refreshPage: () => void;
}

const DateChangeModal = ({ visible, toggle, targetData, refreshPage }: DateChangeModalProps) => {
  const [formData, setFormData] = useState({
    new_date: "",
    new_spot_id: "",
    reason: "",
  });

  const [spotData, setSpotData] = useState<any[]>([]);
  const [selectedSpot, setSelectedSpot] = useState("");
  const [isSpotLoading, setIsSpotLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [eligibilityCheck, setEligibilityCheck] = useState<any>(null);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);

  // Check eligibility when date or spot changes
  const checkEligibility = useCallback(async () => {
    if (!formData.new_date || !targetData?.id) return;

    setIsCheckingEligibility(true);
    try {
      const response = await API.post(`${BACKEND_HOST}/package/date-changes/check_eligibility/`, {
        reservation_id: targetData.id,
        new_date: formData.new_date,
        new_spot_id: formData.new_spot_id || undefined,
      });

      setEligibilityCheck(response.data);
    } catch (error: any) {
      console.error("Eligibility check failed:", error);
      setEligibilityCheck({
        eligible: false,
        reason: error.response?.data?.error || "확인 중 오류가 발생했습니다.",
      });
    } finally {
      setIsCheckingEligibility(false);
    }
  }, [formData.new_date, formData.new_spot_id, targetData?.id]);

  // Load available spots for the selected date
  useEffect(() => {
    if (!targetData?.package_id || !formData.new_date) return;

    setIsSpotLoading(true);
    const url = `${BACKEND_HOST}/package/items/?date=${formData.new_date}`;

    API.get(url)
      .then(({ data }) => {
        if (!data) {
          console.log("data load error");
          return;
        }

        const _package = data.find((item: any) => item.id === Number(targetData.package_id));
        if (!_package) {
          toast.error("해당 상품을 찾을 수 없습니다.");
          return;
        }

        const _seatSelectOptions = _package.spots.map((item: any) => {
          return {
            id: item.id,
            value: item.id,
            name: item.name,
            is_able: item.is_able,
            reason: item.reason,
          };
        });

        setSpotData(_seatSelectOptions);
      })
      .catch((e) => {
        console.error("Failed to load spots:", e);
        toast.error("자리 정보를 불러오는데 실패했습니다.");
      })
      .finally(() => setIsSpotLoading(false));
  }, [formData.new_date, targetData?.package_id]);

  // Check eligibility when form data changes
  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!eligibilityCheck?.eligible) {
      toast.error("날짜 변경이 불가능합니다.");
      return;
    }

    if (!targetData) return;

    setIsSubmitting(true);
    try {
      const url = `${BACKEND_HOST}/package/reservation-date-changes/${targetData.id}/change_date/`;
      const data: any = {
        new_date: formData.new_date,
        reason: formData.reason,
      };

      if (formData.new_spot_id) {
        data.new_spot_id = formData.new_spot_id;
      }

      const response = await API.patch(url, data);

      if (response.data.success) {
        toast.success(response.data.message || "날짜 변경이 완료되었습니다.");
        toggle();
        refreshPage();
      } else {
        toast.error(response.data.error || "날짜 변경에 실패했습니다.");
      }
    } catch (error: any) {
      console.error("Date change failed:", error);
      toast.error(error.response?.data?.error || "날짜 변경에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      setFormData({
        new_date: "",
        new_spot_id: "",
        reason: "",
      });
      setSelectedSpot("");
      setEligibilityCheck(null);
    }
  }, [visible, targetData]);

  return (
    <Modal
      isOpen={visible}
      toggle={toggle}
      centered={true}
      size="lg"
      className="top-3 max-h-[calc(100dvh-1.5rem)] translate-y-0 sm:top-1/2 sm:max-h-[90vh] sm:-translate-y-1/2"
    >
      <ModalBody className="overflow-x-hidden px-4 py-4 sm:px-5">
        <div className="text-left">
          <div className="text-lg sm:px-4">
            <h4>날짜 변경</h4>

            {targetData && (
              <div className="mt-4 rounded bg-light p-3">
                <p className="text-muted mb-2"><strong>상품명:</strong> {targetData.name}</p>
                <p className="text-muted mb-2"><strong>현재 예약일:</strong> {targetData.reservationDate}</p>
                {targetData.reservationEndDate !== targetData.reservationDate && (
                  <p className="text-muted mb-2"><strong>종료일:</strong> {targetData.reservationEndDate}</p>
                )}
                <p className="text-muted mb-0"><strong>자리:</strong> {targetData.spot}번</p>
              </div>
            )}

            <form className="mt-4" onSubmit={(e) => e.preventDefault()}>
              <Row noGutters className="space-y-1 sm:space-y-0">
                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <Label htmlFor="new-date" className="form-label">
                      변경할 날짜 <span className="text-danger">*</span>
                    </Label>
                    <CommonDatePicker
                      id="new-date"
                      className="form-control"
                      options={{
                        minDate: "today",
                      }}
                      placeholder="날짜를 선택하세요"
                      value={formData.new_date}
                      onChangeValue={(newDate) => {
                        setFormData((prev) => ({
                          ...prev,
                          new_date: newDate,
                          new_spot_id: "",
                        }));
                        setSelectedSpot("");
                      }}
                    />
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <Label htmlFor="reason" className="form-label">
                      변경 사유
                    </Label>
                    <Input
                      type="text"
                      id="reason"
                      className="form-control"
                      placeholder="변경 사유를 입력하세요"
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      maxLength={200}
                    />
                  </div>
                </Col>
              </Row>

              {/* Eligibility Check Result */}
              {isCheckingEligibility && (
                <div className="mb-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-3 text-sky-800">
                  <div className="flex items-center gap-2">
                    <Spinner size="sm" />
                    <span className="font-medium">변경 가능 여부를 확인하고 있습니다...</span>
                  </div>
                </div>
              )}

              {eligibilityCheck && (
                <div
                  className={[
                    "mb-3 rounded-lg border px-3 py-3",
                    eligibilityCheck.eligible
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-rose-200 bg-rose-50 text-rose-800",
                  ].join(" ")}
                >
                  <div className="flex items-start gap-2">
                    <i className={`mt-0.5 shrink-0 text-lg ${eligibilityCheck.eligible ? 'bx bx-check-circle' : 'bx bx-x-circle'}`}></i>
                    <span className="font-medium leading-6">{eligibilityCheck.reason}</span>
                  </div>

                  {eligibilityCheck.weather_exception && (
                    <div className="mt-2 pl-7 text-sm leading-6 text-emerald-700">
                      * 예외 상황으로 인한 당일 변경이 승인되었습니다.
                    </div>
                  )}

                  {/* {eligibilityCheck.price_comparison && (
                    <div className="mt-2">
                      <small>
                        기존 가격: {eligibilityCheck.price_comparison.original_price?.toLocaleString()}원 → 
                        변경 가격: {eligibilityCheck.price_comparison.new_price?.toLocaleString()}원
                      </small>
                    </div>
                  )} */}
                </div>
              )}

              {/* Spot Selection */}
              {formData.new_date && eligibilityCheck?.eligible && (
                <Row noGutters>
                  <Col xs={12} md={12}>
                    <div className="mb-3">
                      <Label className="form-label">자리 선택 (선택사항)</Label>
                      {isSpotLoading ? (
                        <div className="text-center p-3">
                          <Spinner size="sm" />
                          <span className="ms-2">자리 정보를 불러오는 중...</span>
                        </div>
                      ) : (
                        <div style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                          marginTop: "10px"
                        }}>
                          {spotData.map((spot, idx) => (
                            <Button
                              key={idx}
                              variant={selectedSpot === spot.name ? "primary" : "secondary"}
                              size="sm"
                              onClick={() => {
                                if (spot.is_able) {
                                  setSelectedSpot(spot.name);
                                  setFormData({ ...formData, new_spot_id: spot.id });
                                }
                              }}
                              disabled={!spot.is_able}
                              style={{
                                minWidth: "40px",
                                height: "40px",
                              }}
                            >
                              {spot.name}
                            </Button>
                          ))}
                        </div>
                      )}
                      <small className="text-muted mt-2 d-block">
                        * 자리를 선택하지 않으면 자동으로 배정됩니다.
                      </small>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Important Notes */}
              <div className="mt-4 p-3 bg-warning bg-opacity-10 rounded">
                <h6 className="text-warning">
                  <i className="bx bx-info-circle me-2"></i>
                  변경 안내사항
                </h6>
                <ul className="mb-0 small text-muted">
                  <li>날짜 변경은 예약일 3일 전까지만 가능합니다.</li>
                  <li>가격 및 예약권 사용 조건에 따라 변경 가능 여부가 결정됩니다.</li>
                  <li>3개월 이내, 예약당 최대 3회까지만 변경 가능합니다.</li>
                  <li>변경 요청은 즉시 처리됩니다.</li>
                </ul>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-4 grid w-full min-w-0 grid-cols-1 gap-2 overflow-x-hidden border-t border-gray-100 pt-4 sm:mb-2 sm:grid-cols-2">
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!eligibilityCheck?.eligible || isSubmitting}
            className="min-w-0 w-full"
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                처리중...
              </>
            ) : (
              "날짜 변경"
            )}
          </Button>

          <Button
            variant="secondary"
            onClick={toggle}
            disabled={isSubmitting}
            className="min-w-0 w-full"
          >
            취소
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default DateChangeModal;
